import React, { useCallback } from 'react';

import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';

import { Button } from '@mui/material';

import type { CsFeature } from './features/registry';
import type { MergedAgentRow } from './types';

interface Props {
  rows: MergedAgentRow[];
  featureIds: string[];
  featureMap: Record<string, CsFeature>;
  dates: string[];
}

function escapeCsvValue(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatDateForCsv(yyyymmdd: string): string {
  if (yyyymmdd.length !== 8) return yyyymmdd;
  return `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(0, 4)}`;
}

/**
 * Exports the currently-visible Agent Actions table to CSV using two header
 * rows: feature labels grouped above their date + Total columns (mirroring
 * the on-screen table), then human-readable DD/MM/YYYY dates underneath.
 */
export default function CsDashboardCsvExport({ rows, featureIds, featureMap, dates }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const handleExportCsv = useCallback(() => {
    if (rows.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    // Row 1: feature group label spans dates + total; cells under the span are blank.
    const featureGroupHeader: string[] = ['Agent'];
    featureIds.forEach((featureId) => {
      const label = featureMap[featureId]?.label ?? featureId;
      featureGroupHeader.push(label);
      for (let i = 0; i < dates.length; i += 1) {
        featureGroupHeader.push('');
      }
    });

    // Row 2: date columns then "Total" under each feature group.
    const dateHeader: string[] = [''];
    featureIds.forEach(() => {
      dates.forEach((date) => dateHeader.push(formatDateForCsv(date)));
      dateHeader.push('Total');
    });

    const csvRows = rows.map((row) => {
      const cells: string[] = [row.agentEmail ?? row.agentId];
      featureIds.forEach((featureId) => {
        const byDate = row.countsByFeatureAndDate[featureId] ?? {};
        let total = 0;
        dates.forEach((date) => {
          const count = byDate[date] ?? 0;
          total += count;
          cells.push(String(count));
        });
        cells.push(String(total));
      });
      return cells;
    });

    const csvContent = [featureGroupHeader, dateHeader, ...csvRows]
      .map((line) => line.map(escapeCsvValue).join(','))
      .join('\n');

    const timestamp = new Date().toISOString().split('T')[0];
    fileDownload(csvContent, `agent-actions-${timestamp}.csv`);
    enqueueSnackbar('CSV exported successfully', { variant: 'success' });
  }, [rows, featureIds, featureMap, dates, enqueueSnackbar]);

  return (
    <Button variant="contained" color="primary" onClick={handleExportCsv}>
      Export CSV
    </Button>
  );
}
